import React from "react";
import { Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, ListItemIcon, Divider, Tooltip, Typography } from "@mui/material";
import {
  Menu as MenuIcon,
  NotificationsNoneOutlined,
  PointOfSaleOutlined,
  DashboardCustomizeOutlined,
  RestartAltOutlined,
  DarkModeOutlined,
  LightModeOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { signOut } from "../../Store/Actions/signOut";
import { useNavigate } from "react-router-dom";
import {
  useDeleteCartItemMutation,
  useLazyGetAllCartItemsQuery,
} from "../../Store/Services/CartService";
import CurrencyInitializer from "../Common/CurrencyInitializer";
import { PresetPreview } from "../Restaurant/CustomizableWorkspace";
import { clearGlobalCartId, getGlobalCartId } from "../../Utils/cartSession";
import styles from "./NavBar.module.css";

const NavBar = ({ onMenuClick, themeMode = "light", onThemeToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const [openLogoutDialog, setOpenLogoutDialog] = React.useState(false);
  const [pendingCartId, setPendingCartId] = React.useState(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [viewPresets, setViewPresets] = React.useState(
    () => (typeof window !== "undefined" && window.__posWorkspacePresets) || []
  );
  const [viewMenuAnchor, setViewMenuAnchor] = React.useState(null);

  React.useEffect(() => {
    const onPresets = (e) => setViewPresets(e.detail?.presets || []);
    window.addEventListener("pos:workspace-presets", onPresets);
    return () => window.removeEventListener("pos:workspace-presets", onPresets);
  }, []);

  const [fetchCartItems] = useLazyGetAllCartItemsQuery();
  const [deleteCartItem] = useDeleteCartItemMutation();

  const getActiveCartId = () => getGlobalCartId();

  const performLogout = () => {
    dispatch(signOut());
    navigate("/pos", { replace: true });
  };

  const loadCartItems = async (cartId) => {
    if (!cartId) {
      return [];
    }
    try {
      const response = await fetchCartItems(cartId).unwrap();
      return response?.orderDetails?.items || [];
    } catch {
      return [];
    }
  };

  const clearCartItems = async (cartId) => {
    const items = await loadCartItems(cartId);
    if (!items.length) {
      return;
    }

    await Promise.all(
      items.map((item) =>
        deleteCartItem(item?.orderDetId)
          .unwrap()
          .catch(() => null)
      )
    );
  };

  const onLogout = async () => {
    if (isBusy) {
      return;
    }

    setIsBusy(true);
    const cartId = getActiveCartId();
    if (!cartId) {
      performLogout();
      setIsBusy(false);
      return;
    }

    const items = await loadCartItems(cartId);
    if (items.length > 0) {
      setPendingCartId(cartId);
      setOpenLogoutDialog(true);
      setIsBusy(false);
      return;
    }

    performLogout();
    setIsBusy(false);
  };

  const onCompleteSale = () => {
    setOpenLogoutDialog(false);
    setPendingCartId(null);
    navigate("/", { replace: true });
    window.dispatchEvent(new CustomEvent("pos:open-checkout-popup"));
  };

  const onAbandonSale = async () => {
    if (isBusy) {
      return;
    }
    setIsBusy(true);
    try {
      await clearCartItems(pendingCartId || getActiveCartId());
      clearGlobalCartId();
      window.dispatchEvent(
        new CustomEvent("pos:cart-id-changed", {
          detail: { cartId: null },
        })
      );
      toast.info("Current sale was abandoned", { position: "bottom-right" });
      setOpenLogoutDialog(false);
      setPendingCartId(null);
      performLogout();
    } catch {
      toast.error("Unable to abandon sale. Try again.", { position: "bottom-right" });
    } finally {
      setIsBusy(false);
    }
  };

  const userName = auth?.user?.name || "Cashier";

  return (
    <header className={`${styles.navBar} ${themeMode === "dark" ? styles.dark : ""}`}>

        <CurrencyInitializer />
        
      <div className={styles.leftRow}>
        <IconButton className={styles.menuButton} onClick={onMenuClick}>
          <MenuIcon />
        </IconButton>
        <div className={styles.brandArea}>
          <div className={styles.brandBadge}>
            <PointOfSaleOutlined fontSize="small" />
          </div>
          <div>
            <Typography className={styles.brandName}>N-ACH POS</Typography>
            <Typography className={styles.brandTag}>Point of Sale</Typography>
          </div>
        </div>
      </div>

      <div className={styles.rightNavContainer}>
        {viewPresets.length > 0 && (
          <>
            <Tooltip title="Layout view">
              <IconButton
                className={styles.iconButton}
                onClick={(e) => setViewMenuAnchor(e.currentTarget)}
              >
                <DashboardCustomizeOutlined className={styles.navIcon} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={viewMenuAnchor}
              open={Boolean(viewMenuAnchor)}
              onClose={() => setViewMenuAnchor(null)}
            >
              {viewPresets.map((p) => (
                <MenuItem
                  key={p.key}
                  onClick={() => {
                    window.dispatchEvent(
                      new CustomEvent("pos:apply-preset", {
                        detail: { key: p.key },
                      })
                    );
                    setViewMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>
                    <PresetPreview layout={p.layout} />
                  </ListItemIcon>
                  {p.label}
                </MenuItem>
              ))}
              <Divider />
              <MenuItem
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("pos:reset-layout"));
                  setViewMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <RestartAltOutlined sx={{ fontSize: 18 }} />
                </ListItemIcon>
                Reset layout
              </MenuItem>
            </Menu>
          </>
        )}
        <IconButton
          className={styles.iconButton}
          onClick={onThemeToggle}
          aria-label={themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          {themeMode === "dark" ? <LightModeOutlined className={styles.navIcon} /> : <DarkModeOutlined className={styles.navIcon} />}
        </IconButton>
        <IconButton className={styles.iconButton}>
          <NotificationsNoneOutlined className={styles.navIcon} />
        </IconButton>
        <Avatar className={styles.userAvatar}>
          {userName.slice(0, 1).toUpperCase()}
        </Avatar>
        <Typography className={styles.userLabel}>{userName}</Typography>
        <Button className={styles.logoutButton} onClick={onLogout}>
          Logout
        </Button>
      </div>

      <Dialog open={openLogoutDialog} onClose={() => setOpenLogoutDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Current Sale In Progress</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#52637f", fontSize: "0.95rem" }}>
            There are items in the current sale. Do you want to complete the sale or abandon it before logout?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenLogoutDialog(false)} disabled={isBusy} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button onClick={onAbandonSale} color="error" variant="outlined" disabled={isBusy} sx={{ textTransform: "none" }}>
            Abandon Sale
          </Button>
          <Button onClick={onCompleteSale} variant="contained" disabled={isBusy} sx={{ textTransform: "none" }}>
            Complete Sale
          </Button>
        </DialogActions>
      </Dialog>
    </header>
  );
};

export default NavBar;